/**
 * Global singleton tracker for object initialization options.
 * Uses WeakMap to store options with weak object keys, allowing
 * options to be garbage collected when the object is no longer referenced.
 */
class ObjectInitOptionsTracker {
  private static _instance: ObjectInitOptionsTracker | null = null;
  private _storage = new WeakMap<object, unknown>();

  private constructor() {}

  static getInstance(): ObjectInitOptionsTracker {
    this._instance ??= new ObjectInitOptionsTracker();
    return this._instance;
  }

  /**
   * Records initialization options for a given object.
   * The options can be retrieved later using the same object reference.
   * When the object is garbage collected, the options become inaccessible.
   * @param obj - The object to use as the weak key
   * @param options - The initialization options to store
   */
  record(obj: object, options: unknown): void {
    if (this.has(obj)) {
      throw new Error(`Options already recorded for object: ${obj}`);
    }
    if (typeof options === 'object' && options !== null) {
      this._storage.set(obj, options);
    }
  }

  /**
   * Retrieves the initialization options for a given object.
   * Returns undefined if no options were recorded or if the object was garbage collected.
   * @param obj - The object used as the key during recording
   * @returns The stored options, or undefined if not found
   */
  retrieve(obj: object): unknown | undefined {
    return this._storage.get(obj) ?? undefined;
  }

  /**
   * Checks if options exist for the given object.
   * Returns false if the object was garbage collected.
   * @param obj - The object to check
   * @returns True if options are stored for this object
   */
  has(obj: object): boolean {
    return this._storage.has(obj);
  }

  /**
   * Deletes the options entry for a given object.
   * @param obj - The object whose options should be removed
   * @returns True if an entry existed and was removed
   */
  delete(obj: object): boolean {
    return this._storage.delete(obj);
  }
}

/** Global singleton instance for convenient access */
export const objectInitOptionsTracker = ObjectInitOptionsTracker.getInstance();
